import { useState, useEffect, useRef } from 'react';
import { isIOS, isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import { SxProps, Theme, useTheme } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckIcon from '@mui/icons-material/Check';
import DSMobileStepper from '../../design-system/DSMobileStepper';
import DSAppBar from '../../design-system/DSAppBar';
import { DSTypography } from '../../design-system/Typography';
import DSBox from '../../design-system/DSBox';
import DSIconButton from '../../design-system/DSIconButton';
import { useGiftContext } from './Context/GiftContextProvider';
import { KLYDO_YELLOW, KLYDO_BLACK } from '../../consts/colors';
import useDisableZoom from './hooks/useDisableZoom';
import useUpsertGiftMutation from './hooks/useUpsertGiftMutation';
import { useNavigateDone } from './hooks/useNavigateDone';
import { useHistory } from './hooks/useHistory';
import GreetLogo from './GreetLogo';
import FinalConfirmationDialog from './FinalConfirmationDialog';
import TermsDialog from './TermsDialog';
import useUrlSyncedModal from './hooks/useUrlSyncedModal';
import {
  StepValidationRule,
  useWizardNavigation,
} from './hooks/useWizardNavigation';
import steps from './Steps/steps';
import { WizardStep } from '../../consts/wizardSteps';
import { isSenderNameOptional } from './utils';

const GiftWizard = () => {
  useDisableZoom();
  const { search } = useHistory();
  const orderName = search.orderName || search.orderId;
  const {
    selectedOccasion,
    klydoGiftProps,
    selectedKlydoGreet,
    loading,
    next,
    back,
    setKlydoGiftProps,
    admin,
    giftCategories,
  } = useGiftContext();
  const senderNameOptional = isSenderNameOptional(
    klydoGiftProps?.pendulumLogoUrl,
    admin,
    giftCategories,
  );

  const step = search.step;
  const activeStep = steps.findIndex((s) => s.id === step);
  const { palette } = useTheme();
  const navigate = useNavigate();
  const navigateToDone = useNavigateDone();
  const { validateAndNavigate } = useWizardNavigation();

  const wizardStepRef = useRef<HTMLDivElement>(null);

  const [finalDialogOpen, setFinalDialogOpen] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  const TERMS_PARAM_KEY = 'terms';
  const TERMS_PARAM_VALUE = 'open';

  const {
    open: termsModalOpen,
    onOpen: openTermsModal,
    onClose: closeTermsModal,
  } = useUrlSyncedModal(TERMS_PARAM_KEY, TERMS_PARAM_VALUE);

  const { mutate: addGiftMutation, isPending: addGiftPending } =
    useUpsertGiftMutation({
      onSuccess: () => {
        if (klydoGiftProps?.orderName) {
          navigateToDone({ orderName: klydoGiftProps.orderName });
        } else if (klydoGiftProps?.clockId) {
          navigateToDone({ clockId: klydoGiftProps.clockId });
        }
      },
    });

  const handleTermsAcceptedChange = (accepted: boolean) => {
    setKlydoGiftProps({
      ...klydoGiftProps,
      giftTerms: {
        accepted,
        signedTimestamp: accepted ? Date.now() : null,
      },
    });
    if (accepted) setShowTermsError(false);
  };

  const validateAndConfirm = () => {
    if (!klydoGiftProps?.giftTerms?.accepted) {
      setShowTermsError(true);
      return;
    }
    setFinalDialogOpen(false);
    addGiftMutation(klydoGiftProps);
  };

  useEffect(() => {
    wizardStepRef.current?.scrollTo(0, 0);
  }, [search.step]);

  useEffect(() => {
    if (!klydoGiftProps?.clockId && !klydoGiftProps?.orderName) {
      navigate(
        `/gift${orderName ? `?orderName=${Array.isArray(orderName) ? orderName[0] : orderName}` : ''}`,
        { replace: true },
      );
      return;
    }

    const validationRules: StepValidationRule[] = [
      {
        stepIndex: 2,
        condition: activeStep >= 2 && !selectedKlydoGreet,
        redirectStep: WizardStep.GREET,
      },
      {
        stepIndex: 3,
        condition:
          activeStep >= 3 && !klydoGiftProps?.senderName && !senderNameOptional,
        redirectStep: WizardStep.PERSONAL_GREET,
      },
    ];

    validateAndNavigate(validationRules, orderName);
  }, [
    activeStep,
    selectedOccasion,
    selectedKlydoGreet,
    klydoGiftProps?.senderName,
    klydoGiftProps?.clockId,
    klydoGiftProps?.orderName,
    orderName,
  ]);

  const handleNext = () => {
    if (
      (activeStep === 0 && selectedOccasion) ||
      (activeStep === 1 && selectedKlydoGreet) ||
      (activeStep === 2 && (klydoGiftProps?.senderName || senderNameOptional))
    ) {
      next();
    }
  };

  const handleButtonDone = () => {
    if (!klydoGiftProps?.senderName && !senderNameOptional) {
      alert('Your name is required');
      return;
    }
    setFinalDialogOpen(true);
  };

  const buttonSx: SxProps<Theme> = {
    textTransform: 'none',
    padding: 2,
    margin: 1,
    color: KLYDO_BLACK,
  };

  return (
    <>
      <FinalConfirmationDialog
        open={finalDialogOpen}
        onClose={() => {
          setShowTermsError(false);
          setFinalDialogOpen(false);
        }}
        onConfirm={validateAndConfirm}
        onOpenTerms={openTermsModal}
        termsAccepted={klydoGiftProps?.giftTerms?.accepted ?? false}
        onTermsAcceptedChange={handleTermsAcceptedChange}
        showTermsError={showTermsError}
        loading={addGiftPending}
      />
      <TermsDialog open={termsModalOpen} onClose={closeTermsModal} />

      <DSBox
        sx={{
          overflow: 'hidden',
          minHeight: { xs: isIOS && isMobile ? 0 : '100vh', md: '100vh' },
          backgroundColor: palette.background.paper,
        }}
      >
        <DSAppBar
          onClick={() => {
            window.location.href = 'https://www.klydoclock.com/';
          }}
          position="fixed"
          color="transparent"
          sx={{
            cursor: 'pointer',
            backgroundColor: palette.background.paper,
            paddingY: isMobile ? '16px' : '32px',
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'center',
            alignItems: 'center',
            boxShadow: 'none',
          }}
        >
          <GreetLogo color={KLYDO_YELLOW} width={isMobile ? 150 : 250} />
        </DSAppBar>

        <DSBox
          ref={wizardStepRef}
          id="gift-wizard-step"
          sx={{
            overflow: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            backgroundColor: palette.background.paper,
            paddingTop: isMobile ? '80px' : '128px',
            height: '100vh',
            paddingBottom: isMobile ? '120px' : '160px',
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            alignItems: 'center',
            justifyContent: 'start',
          }}
        >
          <DSTypography
            sx={{
              lineHeight: 1,
              fontStyle: 'bold',
              fontSize: isMobile ? 35 : 47.52,
              fontFamily: 'Gilroy-Bold',
              maxWidth: isMobile ? '270px' : '1080px',
              color: '#FFFFFF',
            }}
          >
            {steps[activeStep]?.title}
          </DSTypography>
          <DSBox py={0.8} />
          <DSTypography
            sx={{
              fontSize: isMobile ? 15 : 18,
              fontFamily: 'Gilroy-Medium',
              maxWidth: isMobile ? '270px' : '1080px',
              color: '#FFFFFF',
            }}
          >
            {steps[activeStep]?.description}
          </DSTypography>
          {steps[activeStep]?.component}
        </DSBox>

        <DSMobileStepper
          variant="dots"
          position="bottom"
          steps={steps.length}
          activeStep={activeStep}
          sx={{
            width: { xs: '100%', md: 900 },
            margin: 'auto',
            '.MuiMobileStepper-dot': {
              backgroundColor: '#D9D9D9',
              margin: { xs: '5px' },
            },
            '.MuiMobileStepper-dotActive': {
              backgroundColor: KLYDO_YELLOW,
            },
          }}
          nextButton={
            activeStep === 3 ? (
              <DSIconButton
                disabled={addGiftPending}
                onClick={handleButtonDone}
                sx={{
                  ...buttonSx,
                  backgroundColor: KLYDO_YELLOW,
                  width: { xs: 20, md: 60 },
                  height: { xs: 20, md: 60 },
                  borderRadius: '50%',
                  '&:hover': { backgroundColor: KLYDO_YELLOW },
                }}
              >
                <CheckIcon
                  sx={{ fontSize: { xs: 20, md: 30 }, color: KLYDO_BLACK }}
                />
              </DSIconButton>
            ) : (
              <DSIconButton
                onClick={handleNext}
                disabled={
                  (activeStep === 0 && !selectedOccasion) ||
                  (activeStep === 1 && !selectedKlydoGreet) ||
                  (activeStep === 2 &&
                    !klydoGiftProps?.senderName &&
                    !senderNameOptional) ||
                  !!(loading ?? false)
                }
                sx={{
                  ...buttonSx,
                  backgroundColor: KLYDO_YELLOW,
                  width: { xs: 20, md: 60 },
                  height: { xs: 20, md: 60 },
                  borderRadius: '50%',
                  '&:hover': { backgroundColor: KLYDO_YELLOW },
                  '&.Mui-disabled': { backgroundColor: 'rgba(255,216,72,0.3)' },
                }}
              >
                <ArrowForwardIosIcon
                  sx={{ fontSize: { xs: 20, md: 30 }, color: KLYDO_BLACK }}
                />
              </DSIconButton>
            )
          }
          backButton={
            <DSIconButton
              onClick={() => back?.()}
              disabled={activeStep === 0 || !back}
              sx={{
                ...buttonSx,
                backgroundColor: palette.secondary.main,
                width: { xs: 20, md: 60 },
                height: { xs: 20, md: 60 },
                borderRadius: '50%',
                '&:hover': { backgroundColor: palette.secondary.dark },
                '&.Mui-disabled': { backgroundColor: 'rgba(45,45,45,0.3)' },
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: { xs: 20, md: 30 } }} />
            </DSIconButton>
          }
        />
      </DSBox>
    </>
  );
};

export default GiftWizard;
